import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Play,
  RefreshCw,
  MessageSquare,
  Zap,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  Clock,
  Users,
  Activity,
  Settings,
} from "lucide-react";
import { callFunction, subscribeToPath } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { useTestMode } from "../../contexts/TestModeContext";
import { safeDistanceToNow as formatDistanceToNow } from "../../utils/safeFormat";

// All available leagues (must match functions/index.js LEAGUE_TO_ODDS_API)
const ALL_LEAGUES = [
  { id: "nfl", name: "NFL", icon: "🏈" },
  { id: "ncaaf", name: "NCAAF", icon: "🏈" },
  { id: "nba", name: "NBA", icon: "🏀" },
  { id: "ncaab", name: "NCAAB", icon: "🏀" },
  { id: "mlb", name: "MLB", icon: "⚾" },
  { id: "nhl", name: "NHL", icon: "🏒" },
  { id: "mls", name: "MLS", icon: "⚽" },
  { id: "epl", name: "EPL", icon: "⚽" },
  { id: "laliga", name: "La Liga", icon: "⚽" },
  { id: "bundesliga", name: "Bundesliga", icon: "⚽" },
  { id: "seriea", name: "Serie A", icon: "⚽" },
  { id: "ligue1", name: "Ligue 1", icon: "⚽" },
  { id: "ucl", name: "UCL", icon: "⚽" },
  { id: "ufc", name: "UFC", icon: "🥊" },
  { id: "wnba", name: "WNBA", icon: "🏀" },
  { id: "cfl", name: "CFL", icon: "🏈" },
];

interface TaskRunLog {
  functionName: string;
  runAt: number;
  runAtISO: string;
  success: boolean;
  duration: number | null;
  summary: string | null;
  details: any;
  error: string | null;
}

interface ToolResult {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

interface ToolCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  buttonText: string;
  dangerLevel?: "safe" | "caution" | "danger";
  onExecute: () => Promise<ToolResult>;
  disabled?: boolean;
}

const ToolCard: React.FC<ToolCardProps> = ({
  title,
  description,
  icon,
  buttonText,
  dangerLevel = "safe",
  onExecute,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ToolResult | null>(null);

  const handleExecute = async () => {
    setIsLoading(true);
    setResult(null);
    try {
      const res = await onExecute();
      setResult(res);
    } catch (error: any) {
      const errorCode = error.code || error.name || 'unknown';
      const errorMessage = error.message || "An error occurred";
      const errorDetails = error.details || error.serverResponse || null;

      console.error('Tool execution error:', { errorCode, errorMessage, errorDetails, fullError: error });

      setResult({
        success: false,
        error: `[${errorCode}] ${errorMessage}`,
        data: errorDetails ? { details: errorDetails } : undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const buttonColors = {
    safe: "bg-primary hover:bg-primary-dark",
    caution: "bg-yellow-500 hover:bg-yellow-600",
    danger: "bg-red-500 hover:bg-red-600",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            {title}
          </h3>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
            {description}
          </p>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleExecute}
              disabled={isLoading || disabled}
              className={`
                px-4 py-2 rounded-lg text-white font-medium transition-colors
                flex items-center gap-2
                ${buttonColors[dangerLevel]}
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play size={16} />
                  {buttonText}
                </>
              )}
            </button>

            {dangerLevel === "caution" && (
              <span className="text-xs text-yellow-500 flex items-center gap-1">
                <AlertTriangle size={12} />
                Use with caution
              </span>
            )}
          </div>

          {/* Result Display */}
          {result && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className={`mt-4 p-3 rounded-lg ${
                result.success
                  ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                  : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
              }`}
            >
              <div className="flex items-start gap-2">
                {result.success ? (
                  <CheckCircle size={16} className="text-green-500 mt-0.5" />
                ) : (
                  <XCircle size={16} className="text-red-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <p
                    className={`text-sm font-medium ${
                      result.success ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"
                    }`}
                  >
                    {result.success ? "Success" : "Error"}
                  </p>
                  {result.message && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {result.message}
                    </p>
                  )}
                  {result.error && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                      {result.error}
                    </p>
                  )}
                  {result.data && (
                    <pre className="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded mt-2 overflow-auto max-h-32">
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Scheduled Task Health Row
const TaskHealthRow: React.FC<{
  name: string;
  label: string;
  schedule: string;
  log: TaskRunLog | null;
}> = ({ name, label, schedule, log }) => {
  const getStatusColor = () => {
    if (!log) return "text-gray-400";
    if (!log.success) return "text-red-500";
    // Warn if last run was too long ago
    const hoursSince = (Date.now() - log.runAt) / (1000 * 60 * 60);
    if (name === "dailyPicksCleanup" && hoursSince > 36) return "text-yellow-500";
    if (name === "monthlyGamesFetch" && hoursSince > 744) return "text-yellow-500"; // > 31 days
    return "text-green-500";
  };

  const getStatusIcon = () => {
    if (!log) return <Clock size={16} className="text-gray-400" />;
    if (!log.success) return <XCircle size={16} className="text-red-500" />;
    return <CheckCircle size={16} className={getStatusColor()} />;
  };

  return (
    <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <div className="flex items-center gap-3">
        {getStatusIcon()}
        <div>
          <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
            {label}
          </p>
          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            {schedule}
          </p>
        </div>
      </div>
      <div className="text-right">
        {log ? (
          <>
            <p className={`text-xs font-medium ${log.success ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
              {log.success ? "OK" : "FAILED"}
              {log.duration ? ` (${(log.duration / 1000).toFixed(1)}s)` : ""}
            </p>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              {formatDistanceToNow(log.runAt, { addSuffix: true })}
            </p>
            {log.summary && (
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5 max-w-xs truncate">
                {log.summary}
              </p>
            )}
            {log.error && (
              <p className="text-xs text-red-500 mt-0.5 max-w-xs truncate" title={log.error}>
                {log.error}
              </p>
            )}
          </>
        ) : (
          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            Never run
          </p>
        )}
      </div>
    </div>
  );
};

export const SystemToolsTab: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { isTestMode, getPath } = useTestMode();
  const [latestRuns, setLatestRuns] = useState<Record<string, TaskRunLog>>({});
  const [autoFetchLeagues, setAutoFetchLeagues] = useState<string[]>([]);
  const [isSavingLeagues, setIsSavingLeagues] = useState(false);
  const [leagueSaveResult, setLeagueSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  // Subscribe to latest run logs
  useEffect(() => {
    const unsub = subscribeToPath(getPath("systemLogs/latestRun"), (data) => {
      if (data) {
        setLatestRuns(data as Record<string, TaskRunLog>);
      }
    });

    return () => unsub();
  }, [getPath]);

  // Subscribe to auto-fetch leagues config
  useEffect(() => {
    const unsub = subscribeToPath(getPath("config/autoFetchLeagues"), (data) => {
      if (data && Array.isArray(data)) {
        setAutoFetchLeagues(data);
      } else {
        // Default leagues
        setAutoFetchLeagues([
          "nfl", "ncaaf", "nba", "ncaab", "mlb", "nhl", "mls",
          "epl", "laliga", "bundesliga", "seriea", "ligue1", "ufc"
        ]);
      }
    });

    return () => unsub();
  }, [getPath]);

  const toggleLeague = (leagueId: string) => {
    setAutoFetchLeagues((prev) =>
      prev.includes(leagueId)
        ? prev.filter((l) => l !== leagueId)
        : [...prev, leagueId]
    );
  };

  const saveLeagueConfig = async () => {
    setIsSavingLeagues(true);
    setLeagueSaveResult(null);
    try {
      const response = await callFunction("updateAutoFetchLeagues")({ leagues: autoFetchLeagues });
      const data = (response as any).data || response;
      setLeagueSaveResult({
        success: true,
        message: `Saved ${data.leagues?.length || autoFetchLeagues.length} leagues (~${data.creditEstimate || autoFetchLeagues.length} credits/week)`,
      });
      setTimeout(() => setLeagueSaveResult(null), 5000);
    } catch (error: any) {
      setLeagueSaveResult({
        success: false,
        message: error.message || "Failed to save",
      });
      setTimeout(() => setLeagueSaveResult(null), 5000);
    } finally {
      setIsSavingLeagues(false);
    }
  };

  const SCHEDULED_TASKS = [
    { name: "monthlyGamesFetch", label: "Monthly Games Fetch", schedule: "1st of month 3:00 AM UTC" },
    { name: "dailyPicksCleanup", label: "Daily Picks Cleanup", schedule: "Every day 4:00 AM UTC" },
    { name: "manageSweatroomLifecycle", label: "Sweatroom Lifecycle", schedule: "Every 5 minutes" },
    { name: "manualAutoClose", label: "Manual Auto-Close (last run)", schedule: "Triggered manually" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
            System Tools
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary-dark mt-1">
            Admin functions to manage sweatrooms, live scores, and system health
          </p>
        </div>
      </div>

      {/* Auth Status */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-sm space-y-1">
        <div className="text-text-secondary-light dark:text-text-secondary-dark">
          Logged in as: <strong className="text-text-primary-light dark:text-text-primary-dark">{user?.email || 'Not logged in'}</strong>
          {' | '}
          Admin (local): <strong className={isAdmin ? 'text-green-500' : 'text-red-500'}>{isAdmin ? 'Yes' : 'No'}</strong>
        </div>
        <div className="text-text-secondary-light dark:text-text-secondary-dark text-xs">
          UID: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{user?.uid || 'N/A'}</code>
          {' | '}
          Admin path: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">v1/users/admins/{user?.uid || 'N/A'}</code>
        </div>
        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-2">
          Note: Functions require your UID to exist at the admin path with value <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">true</code> in Firebase.
        </p>
      </div>

      {!user && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <XCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="font-medium text-red-800 dark:text-red-200">
                Not Authenticated
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                You must be logged in to use these tools. Firebase callable functions require authentication.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled Tasks Health Dashboard */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center gap-2">
          <Activity size={20} className="text-primary" />
          Scheduled Tasks Health
        </h3>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {SCHEDULED_TASKS.map((task) => (
            <TaskHealthRow
              key={task.name}
              name={task.name}
              label={task.label}
              schedule={task.schedule}
              log={latestRuns[task.name] || null}
            />
          ))}
        </div>
      </div>

      {/* Auto-Fetch Leagues Config */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center gap-2">
          <Settings size={20} className="text-primary" />
          Weekly Auto-Fetch Leagues
        </h3>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-3">
            Select which leagues are automatically fetched every Monday. Each league costs 1 API credit.
            Currently: {autoFetchLeagues.length} leagues = ~{autoFetchLeagues.length} credits/week.
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {ALL_LEAGUES.map((league) => {
              const isActive = autoFetchLeagues.includes(league.id);
              return (
                <button
                  key={league.id}
                  onClick={() => toggleLeague(league.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                    isActive
                      ? "bg-primary text-black"
                      : "bg-gray-100 dark:bg-gray-700 text-text-secondary-light dark:text-text-secondary-dark hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  <span>{league.icon}</span>
                  {league.name}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={saveLeagueConfig}
              disabled={isSavingLeagues || !user}
              className="px-4 py-2 rounded-lg text-white font-medium bg-primary hover:bg-primary-dark transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {isSavingLeagues ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Configuration"
              )}
            </button>
            {leagueSaveResult && (
              <span className={`text-xs flex items-center gap-1 ${leagueSaveResult.success ? "text-green-600" : "text-red-600"}`}>
                {leagueSaveResult.success ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {leagueSaveResult.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
              Admin Functions
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              These tools trigger Cloud Functions that can modify data. Use responsibly.
            </p>
          </div>
        </div>
      </div>

      {/* Tools Grid */}
      <div className="grid gap-6">
        {/* Sweatroom Management Section */}
        <div>
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center gap-2">
            <MessageSquare size={20} className="text-primary" />
            Sweatroom Management
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <ToolCard
              title="Reopen Community Chats"
              description="Re-opens any community chat rooms that were incorrectly closed. Community chats (community-*) should always be live."
              icon={<Users size={24} className="text-primary" />}
              buttonText="Reopen Chats"
              dangerLevel="safe"
              disabled={!user}
              onExecute={async () => {
                console.log('Calling reopenCommunityChats...');
                const response = await callFunction('reopenCommunityChats')({});
                console.log('reopenCommunityChats response:', response);
                const result = response.data;
                return {
                  success: result.success,
                  message: result.message || `Re-opened ${result.reopenedCount || 0} community chat(s)`,
                  data: result,
                };
              }}
            />

            <ToolCard
              title="Close Ended Picks/Slips"
              description="Close picks and slips whose sweatrooms have ended. Also closes orphaned picks older than 24 hours. Uses test-mode paths when test mode is enabled."
              icon={<Clock size={24} className="text-primary" />}
              buttonText="Run Cleanup"
              dangerLevel="caution"
              disabled={!user}
              onExecute={async () => {
                console.log('Calling manualAutoClose with isTestMode:', isTestMode);
                const response = await callFunction('manualAutoClose')({ isTestMode });
                console.log('manualAutoClose response:', response);
                const result = response.data;
                return {
                  success: result?.success ?? true,
                  message: `Closed ${result?.closedCount || 0} picks, ${result?.orphanedCount || 0} orphaned picks, ${result?.slipsOrphanedCount || 0} slips`,
                  data: result,
                };
              }}
            />
          </div>
        </div>

        {/* Cleanup Section */}
        <div>
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center gap-2">
            <RefreshCw size={20} className="text-primary" />
            Cleanup & Maintenance
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <ToolCard
              title="Daily Picks Cleanup"
              description="Run the daily cleanup to close picks/slips that are still pending/active but their sweatroom has ended. Same as 'Close Ended Picks/Slips' above."
              icon={<CheckCircle size={24} className="text-primary" />}
              buttonText="Run Cleanup"
              dangerLevel="caution"
              disabled={!user}
              onExecute={async () => {
                console.log('Calling manualAutoClose (daily cleanup) with isTestMode:', isTestMode);
                const response = await callFunction('manualAutoClose')({ isTestMode });
                console.log('manualAutoClose response:', response);
                const result = response.data;
                return {
                  success: result?.success ?? true,
                  message: `Closed ${result?.closedCount || 0} picks, ${result?.orphanedCount || 0} orphaned, ${result?.slipsOrphanedCount || 0} slips`,
                  data: result,
                };
              }}
            />
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 mt-8">
        <h3 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-4">
          System Architecture
        </h3>
        <div className="space-y-4 text-sm text-text-secondary-light dark:text-text-secondary-dark">
          <div>
            <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Automated (Scheduled)</h4>
            <div className="space-y-2 ml-4">
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-primary" />
                <span><strong>monthlyGamesFetch</strong> - 1st of month 3 AM UTC, fetches games for configured leagues</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-primary" />
                <span><strong>dailyPicksCleanup</strong> - Daily at 4 AM UTC, closes expired picks/slips</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-primary" />
                <span><strong>manageSweatroomLifecycle</strong> - Every 5 min, transitions sweatroom states</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Event-Driven (Cloud Tasks)</h4>
            <div className="space-y-2 ml-4">
              <div className="flex items-center gap-3">
                <Zap size={16} className="text-success" />
                <span><strong>processLiveScoreUpdate</strong> - Fetches scores per sweatroom every 10 min while live</span>
              </div>
              <div className="flex items-center gap-3">
                <Zap size={16} className="text-success" />
                <span><strong>getGamesForLeague</strong> - On-demand game fetch with 6-hour cache</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemToolsTab;
